/**
 * FloorPlanConstants.js
 * Centralised constants for the Floor Plan Designer feature.
 * Extracted from FloorPlanDesignerPage.jsx to keep the main page component
 * at a manageable size and to allow reuse across sub-components.
 */

import {
    FaDrawPolygon, FaDoorOpen, FaWindowMaximize, FaVectorSquare,
    FaRestroom
} from 'react-icons/fa';
import { BiRectangle } from 'react-icons/bi';
import {
    GiIonicColumn, GiElevator, GiEscalator, GiFireExtinguisher, GiWaterTank
} from 'react-icons/gi';
import { TbStairs, TbDoorEnter } from 'react-icons/tb';
import { IoMdPin } from 'react-icons/io';
import {
    MdElectricalServices, MdWaterDrop, MdLocalParking, MdLocalAtm,
    MdInfoOutline, MdLocalShipping, MdVideocam, MdSpeed, MdSensors,
    MdFireHydrantAlt
} from 'react-icons/md';

// ── Zoom & Snap ───────────────────────────────────────────────────────────────

/** Pixel radius within which a new point will snap to an existing geometry point. */
export const SNAP_THRESHOLD = 10;

/** Minimum zoom scale that can be applied to the canvas stage. */
export const MIN_ZOOM = 0.1;

/** Maximum zoom scale that can be applied to the canvas stage. */
export const MAX_ZOOM = 5;

/** Step increment applied on each zoom-in / zoom-out action. */
export const ZOOM_STEP = 0.1;

// ── Layer Definitions ─────────────────────────────────────────────────────────

/**
 * Describes the display properties for each supported layer type.
 * These mirror the LayerType strings stored in FloorPlanLayer.LayerType on the server.
 */
export const LAYER_TYPES = {
    Structural: { label: 'Structural', color: '#333333', icon: '🏗️' },
    RentableUnits: { label: 'Rentable Units', color: '#4299E1', icon: '🏪' },
    PromotionalAreas: { label: 'Promotional Areas', color: '#48BB78', icon: '📢' },
    MediaSpaces: { label: 'Media Spaces', color: '#ED8936', icon: '📺' },
    Assets: { label: 'Assets', color: '#9F7AEA', icon: '⚙️' }
};

// ── Element Type Definitions ──────────────────────────────────────────────────

/**
 * Metadata for every element type that can be drawn on the canvas.
 * The `icon` property is a React-Icons component (not an instance) so it can
 * be rendered by both the toolbar buttons and the right-panel property sheet.
 * The `layer` property specifies which LAYER_TYPES key this element belongs to.
 */
export const ELEMENT_TYPES = {
    // Building structure
    Wall: { label: 'Wall', icon: FaDrawPolygon, layer: 'Structural' },
    Door: { label: 'Door', icon: FaDoorOpen, layer: 'Structural' },
    Window: { label: 'Window', icon: FaWindowMaximize, layer: 'Structural' },
    Room: { label: 'Room', icon: BiRectangle, layer: 'Structural' },
    Column: { label: 'Column', icon: GiIonicColumn, layer: 'Structural' },
    Entrance: { label: 'Entrance', icon: TbDoorEnter, layer: 'Structural' },
    // Vertical circulation
    Stairs: { label: 'Stairs', icon: TbStairs, layer: 'Structural' },
    Elevator: { label: 'Elevator', icon: GiElevator, layer: 'Structural' },
    Escalator: { label: 'Escalator', icon: GiEscalator, layer: 'Structural' },
    // Facilities
    Restroom: { label: 'Restroom', icon: FaRestroom, layer: 'Structural' },
    FireExit: { label: 'Fire Exit', icon: GiFireExtinguisher, layer: 'Structural' },
    FireHydrant: { label: 'Fire Hydrant', icon: MdFireHydrantAlt, layer: 'Assets' },
    Parking: { label: 'Parking', icon: MdLocalParking, layer: 'Structural' },
    LoadingDock: { label: 'Loading Dock', icon: MdLocalShipping, layer: 'Structural' },
    ATM: { label: 'ATM', icon: MdLocalAtm, layer: 'Assets' },
    InfoKiosk: { label: 'Info Kiosk', icon: MdInfoOutline, layer: 'Assets' },
    // Services & utilities
    DBBoard: { label: 'DB Board', icon: MdElectricalServices, layer: 'Assets' },
    MeteringUnit: { label: 'Metering Unit', icon: MdSpeed, layer: 'Assets' },
    Borehole: { label: 'Borehole', icon: GiWaterTank, layer: 'Assets' },
    WaterPoint: { label: 'Water Point', icon: MdWaterDrop, layer: 'Assets' },
    CCTV: { label: 'CCTV Camera', icon: MdVideocam, layer: 'Assets' },
    Sensor: { label: 'Sensor', icon: MdSensors, layer: 'Assets' },
    // Annotation
    Label: { label: 'Text Label', icon: FaDrawPolygon, layer: 'Structural' },
    Marker: { label: 'Marker', icon: IoMdPin, layer: 'Structural' },
    // Leasable / commercial
    RentableUnit: { label: 'Rentable Unit', icon: FaVectorSquare, layer: 'RentableUnits' },
    PromotionalArea: { label: 'Promo Area', icon: IoMdPin, layer: 'PromotionalAreas' },
    MediaSpace: { label: 'Media Space', icon: IoMdPin, layer: 'MediaSpaces' },
    Asset: { label: 'Asset', icon: IoMdPin, layer: 'Assets' }
};

// ── Tool Names ────────────────────────────────────────────────────────────────

/**
 * Enum-like object of all available drawing tools.
 * The active tool determines how mouse events are interpreted on the canvas.
 */
export const TOOLS = {
    Select: 'Select',
    Pan: 'Pan',
    Delete: 'Delete',
    // Structure
    Wall: 'Wall',
    Door: 'Door',
    Window: 'Window',
    Room: 'Room',
    Column: 'Column',
    Entrance: 'Entrance',
    // Circulation
    Stairs: 'Stairs',
    Elevator: 'Elevator',
    Escalator: 'Escalator',
    // Facilities
    Restroom: 'Restroom',
    FireExit: 'FireExit',
    FireHydrant: 'FireHydrant',
    Parking: 'Parking',
    LoadingDock: 'LoadingDock',
    ATM: 'ATM',
    InfoKiosk: 'InfoKiosk',
    // Utilities
    DBBoard: 'DBBoard',
    MeteringUnit: 'MeteringUnit',
    Borehole: 'Borehole',
    WaterPoint: 'WaterPoint',
    CCTV: 'CCTV',
    Sensor: 'Sensor',
    // Annotation
    Label: 'Label',
    Marker: 'Marker',
    // Commercial layers
    RentableUnit: 'RentableUnit',
    PromotionalArea: 'PromotionalArea',
    MediaSpace: 'MediaSpace',
    Asset: 'Asset'
};
